import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import InterpreterModeRoundedIcon from "@mui/icons-material/InterpreterModeRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import NewReleasesRoundedIcon from "@mui/icons-material/NewReleasesRounded";
import { useNavigate } from "react-router-dom";
import Overlay from "components/Overlay/Overlay";
import { useAuth } from "contexts/AuthContext";

/**
 * WhatChangedDialog — "here is what moved, and where to find it".
 *
 * Rendered with the portal's standard Overlay (not a bare MUI Dialog) so it
 * behaves like every other popup: grey shell, white content card, draggable,
 * maximizable, and it remembers where it was left.
 *
 * SHOWN ONCE PER USER
 * The seen-marker is a localStorage key that includes BOTH the member id and
 * the release id, so:
 *   - a second member signing in on the same browser sees it (id differs),
 *   - the next release that needs announcing can bump `RELEASE_ID` and every
 *     member sees the new notice exactly once,
 *   - a dismissed notice never comes back on reload.
 * localStorage (not a DB column) is deliberate: this is a per-device courtesy
 * notice, and a migration for it would outlive its usefulness. The same
 * content stays readable forever in Help → Changelog, which is where someone
 * who dismissed it will look.
 */

// Bump this when the notice content changes. It is NOT the app version — it is
// the identity of "this particular announcement".
const RELEASE_ID = "2026-10-30-dashboard-split";

const SEEN_KEY_PREFIX = "ce_portal_whats_new_seen";

const buildSeenKey = (memberId) =>
  `${SEEN_KEY_PREFIX}:${RELEASE_ID}:${memberId || "anon"}`;

const readSeen = (memberId) => {
  try {
    return window.localStorage.getItem(buildSeenKey(memberId)) === "1";
  } catch {
    // Private mode / storage disabled: show the notice rather than crash.
    return false;
  }
};

const writeSeen = (memberId) => {
  try {
    window.localStorage.setItem(buildSeenKey(memberId), "1");
  } catch {
    // Ignore — the notice may reappear, which is the safe failure.
  }
};

/**
 * One destination row.
 *
 * The WHOLE row is the button. The first version put a 32px icon-only
 * IconButton on the right, which rendered as a small round control with a
 * fuzzy elevation under it — it read as an odd blob and made a clickable row
 * look only half-interactive. Now the row itself is the target, the icon sits
 * in a flat 8px-radius tile, and no elevation is used anywhere.
 */
const DestinationRow = ({ icon, title, body, onGo, theme, cta }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      role={onGo ? "button" : undefined}
      tabIndex={onGo ? 0 : undefined}
      aria-label={onGo ? `Open ${title}` : undefined}
      onClick={onGo}
      onKeyDown={
        onGo
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onGo();
              }
            }
          : undefined
      }
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        p: 1.5,
        borderRadius: "12px",
        border: `1px solid ${
          hovered && onGo
            ? theme.palette.primary.main
            : theme.palette.primary.border2
        }`,
        bgcolor: theme.palette.background.default,
        cursor: onGo ? "pointer" : "default",
        // No elevation anywhere in this component: a shadow on a hover-only
        // surface is what made the original buttons look wrong.
        boxShadow: "none",
        transition: "border-color 0.15s ease",
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          color: theme.palette.primary.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "8px",
          bgcolor: theme.palette.action.hover,
          boxShadow: "none",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            display: "block",
            mt: 0.25,
          }}
        >
          {body}
        </Typography>
      </Box>

      {onGo ? (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            flexShrink: 0,
            color: theme.palette.primary.main,
            opacity: hovered ? 1 : 0.65,
            transition: "opacity 0.15s ease",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
          >
            {cta || "Open"}
          </Typography>
          <OpenInNewRoundedIcon sx={{ fontSize: 15 }} />
        </Stack>
      ) : null}
    </Box>
  );
};

const WhatChangedDialog = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const memberId = currentUser?.id;

  const [open, setOpen] = useState(false);

  // Decide once per mount/user. `memberId` is undefined until auth resolves,
  // and the key depends on it — so this must re-evaluate when the id appears.
  useEffect(() => {
    if (!memberId) return;
    setOpen(!readSeen(memberId));
  }, [memberId]);

  const close = () => {
    writeSeen(memberId);
    setOpen(false);
  };

  const goTo = (path) => {
    close();
    navigate(path);
  };

  const isAdmin = String(currentUser?.Role || "").toLowerCase() === "admin";

  // Admin-only destinations are not advertised to members — sending someone to
  // an Admin-only route is worse than not mentioning it.
  const destinations = useMemo(() => {
    const rows = [
      {
        key: "dashboard",
        icon: <DashboardRoundedIcon fontSize="small" />,
        title: "Dashboard — what needs action today",
        body:
          "No month picker. Deadlines already missed, work due today, interpretations never closed, and any assignment failing its automatic checks.",
        path: "/dashboard",
        cta: "Open",
      },
      {
        key: "clients",
        icon: <PeopleRoundedIcon fontSize="small" />,
        title: "Clients — figures in the list",
        body:
          "The client, requestor and sub-account totals plus the type / service / contract donuts now open from a button above the Clients grid.",
        path: "/clients",        cta: "Go",      },
      {
        key: "linguists",
        icon: <InterpreterModeRoundedIcon fontSize="small" />,
        title: "Linguists — figures in the list",
        body:
          "Linguist and language totals plus the status / certification / contract / badge / W9 donuts now open from a button above the Linguists grid.",
        path: "/linguists",        cta: "Go",      },
    ];

    if (isAdmin) {
      rows.push({
        key: "reports",
        icon: <AssessmentRoundedIcon fontSize="small" />,
        title: "Reports — the deep analysis",
        body:
          "Revenue, invoices, expenses, clients, linguists and service operations, with date ranges and exports. Nothing was removed from it.",
        path: "/reports",
        cta: "Go",
      });
    }

    return rows;
  }, [isAdmin]);

  return (
    <Overlay
      id="whats-new-notice"
      open={open}
      onClose={close}
      title="What's new"
      titleIcon={<NewReleasesRoundedIcon fontSize="small" />}
      maxWidthPx={760}
      // fit-content: the notice is as tall as its content, never a tall empty
      // shell. The Overlay still allows dragging and maximizing.
      height="fit-content"
      footer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={() => goTo("/help")}
            sx={{
              textTransform: "none",
              color: theme.palette.primary.main,
              boxShadow: "none",
            }}
          >
            See all changes
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={close}
              sx={{
                textTransform: "none",
                color: theme.palette.primary.main,
                boxShadow: "none",
              }}
            >
              Got it
            </Button>
            <Button
              onClick={() => goTo("/dashboard")}
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                bgcolor: theme.palette.primary.main,
                color: theme.palette.background.default,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                  boxShadow: "none",
                },
              }}
            >
              Open the new Dashboard
            </Button>
          </Stack>
        </Box>
      }
    >
      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
          Dashboard is now about today
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 1.5 }}
        >
          The monthly and weekly figures did not disappear — each set now sits
          next to the list it describes, and the deep analysis stays in Reports.
        </Typography>

        <Stack spacing={1}>
          {destinations.map((row) => (
            <DestinationRow
              key={row.key}
              icon={row.icon}
              title={row.title}
              body={row.body}
              cta={row.cta}
              theme={theme}
              onGo={() => goTo(row.path)}
            />
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              <strong>Where did the old Dashboard go?</strong> It is still
              available at <code>/dashboard-legacy</code> while the last numbers
              are moved out of it. It is no longer linked in the sidebar, so
              bookmarks are the only way in — use the destinations above
              instead.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              The full list of changes for this release is in Help → Changelog,
              which you can open any time from the sidebar.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 1.5 }}>
          <Chip
            size="small"
            label="This notice appears once per person"
            sx={{ borderRadius: "8px", fontWeight: 600, fontSize: "0.7rem" }}
          />
        </Box>
      </Box>
    </Overlay>
  );
};

export default WhatChangedDialog;
